import React, {useEffect, useState} from 'react';
import {StyleSheet, View, ScrollView, TouchableOpacity} from 'react-native';
import CCColors from '@/Utils/CCColors';
import CCText from '@/Atoms/CCText';
import {Image as RNImage} from 'react-native';
import NoSummary from '@/Assets/Images/Svgs/Summary.svg';
import {FontSizes, vs, WINDOW_HEIGHT} from '@/Utils/Dimensions';
import useGetClassroomSummaryApi from '@/Hooks/ClassroomInfoApi/useGetClassroomSummaryApi';
import CCSelect from '@/Atoms/CCSelect';
import Box from '@/Atoms/Box';
import Loader from '@/Atoms/CCLoader/CCLoader';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

interface MoveAnalysis {
  move_number: number;
  correct_move: string;
  correct_move_uci: string;
  correct_percentage: number;
  top_moves: Array<{
    move: string;
    percentage: number;
  }>;
}

interface PuzzleSummary {
  puzzle_id: number;
  puzzle_name: string;
  total_moves: number;
  moves_analysis: MoveAnalysis[];
}

interface SummaryResponse {
  webinar_id: string;
  occurence_id: string;
  puzzles: PuzzleSummary[];
}

interface SummaryProps {
  meetingId: string;
  occurenceId: string;
  currentActivePuzzle: any;
  meetingRole: string;
  solvedActivePuzzle: boolean;
  activeTabRight: number;
  source: string;
  
}

const Summary: React.FC<SummaryProps> = ({
  meetingId,
  occurenceId,
  currentActivePuzzle,
  meetingRole,
  solvedActivePuzzle,
  activeTabRight,
  source

}) => {
  const [puzzleSummary, setPuzzleSummary] = useState<PuzzleSummary[]>([]);
  const [selectedSummary, setSelectedSummary] = useState({
    puzzle_id: '',
    puzzle_name: '',
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshSummary, setRefreshSummary] = useState<boolean>(false);

  const handleSummaryChange = (values: string[]) => {
    const id = values[0];
    const summary = puzzleSummary?.find(p => p.puzzle_id.toString() === id);
    if (summary) {
      setSelectedSummary({
        puzzle_id: summary.puzzle_id.toString(),
        puzzle_name: summary.puzzle_name,
      });
    }
  };

  const handleOnSuccessGetSummary = (data: SummaryResponse) => {
    console.log('handleOnSuccessGetSummary data', data);
    console.log('Props Summary', currentActivePuzzle);
    let puzzles = data?.puzzles || [];
    console.log('Summary reaches here 1', meetingRole, solvedActivePuzzle);
    if (meetingRole === 'student' && solvedActivePuzzle === false) {
      console.log('Summary reaches here 2');
      puzzles = puzzles.filter(
        puzzle => puzzle.puzzle_id !== currentActivePuzzle?.puzzleId,
      );
    }

    setPuzzleSummary(puzzles);

    // Auto-select first puzzle if available
    if (puzzles.length > 0) {
      setSelectedSummary({
        puzzle_id: puzzles[0].puzzle_id.toString(),
        puzzle_name: puzzles[0].puzzle_name,
      });
    }
    setLoading(false);
  };

  const handleOnErrorGetSummary = (error: any) => {
    console.error('Error fetching summary:', error);
    setLoading(false);
  };

  const {mutate: getSummary, isLoading} = useGetClassroomSummaryApi(
    handleOnSuccessGetSummary,
    handleOnErrorGetSummary,
  );

  useEffect(() => {
    setLoading(true);
    if (source == 'recording') {
      getSummary({ meetingId, occurenceId, puzzleId: currentActivePuzzle.puzzleId || 0 });
    } else {
      getSummary({ meetingId, occurenceId });
    }
    setRefreshSummary(false);
  }, [meetingId, occurenceId, refreshSummary, activeTabRight]);

  // Get currently selected puzzle data
  const getCurrentPuzzleData = (): PuzzleSummary | null => {
    return (
      puzzleSummary.find(
        p => p.puzzle_id.toString() === selectedSummary.puzzle_id,
      ) || null
    );
  };

  const renderMoveAnalysis = (moveData: MoveAnalysis) => {
    return (
      <View key={moveData.move_number} style={styles.moveSection}>
        <CCText fontSize={FontSizes[14]} style={styles.moveTitle}>
          Move {moveData.move_number}
        </CCText>

        {/* Correct Move */}
        <View style={styles.correctMoveContainer}>
          <CCText fontSize={FontSizes[13]} style={styles.correctMoveText}>
            {moveData.correct_move} is correct
          </CCText>
          <CCText fontSize={FontSizes[12]} style={styles.percentageText}>
            Played by {moveData.correct_percentage}%
          </CCText>
        </View>

        {/* Other Top Moves - includes correct move */}
        {moveData.top_moves.length > 0 && (
          <View style={styles.otherMovesSection}>
            <CCText fontSize={FontSizes[12]} style={styles.otherMovesTitle}>
              Other top moves
            </CCText>
            {moveData.top_moves.map((move, index) => (
              <View key={index} style={styles.otherMoveItem}>
                <CCText fontSize={FontSizes[12]} style={styles.otherMoveText}>
                  {move.move} – Played by {move.percentage}%
                </CCText>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  if (loading || isLoading) {
    return (
      <View style={styles.container}>
        <Loader loading={loading} bgWhite={true} />
      </View>
    );
  }

  if (!puzzleSummary || puzzleSummary.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.emptyState}>
          <RNImage
            resizeMode="contain"
            style={styles.noSummaryImage}
            source={NoSummary}
          />
          <CCText fontSize={FontSizes[18]} style={styles.emptyTitle}>
            No Summary available
          </CCText>
          <CCText fontSize={FontSizes[14]} style={styles.emptyDescription}>
            Summary will appear after students solve puzzles.
          </CCText>
        </View>
      </View>
    );
  }

  const currentPuzzle = getCurrentPuzzleData();

  return (
    <View style={styles.container}>
      {/* Header with Title and Refresh */}
      <View style={styles.header}>
        <CCText fontSize={FontSizes[16]} style={styles.title}>
          Summary
        </CCText>
        <TouchableOpacity onPress={() => setRefreshSummary(true)}>
          <Icon name="reload" color={CCColors.Primary.Brown} size={22} />
        </TouchableOpacity>
      </View>

      {/* Puzzle Selector */}
      <View style={styles.selectorContainer}>
        <CCSelect
          key={selectedSummary.puzzle_id || 'none'}
          ccSelectHeight={45}
          ccSelectWidth="100%"
          singleSelectDropdown={true}
          placeholder={selectedSummary.puzzle_name || 'Select a Puzzle'}
          selectedValues={
            selectedSummary.puzzle_id ? [selectedSummary.puzzle_id] : []
          }
          onValueChange={handleSummaryChange}
          items={puzzleSummary.map(summary => ({
            label: summary.puzzle_name,
            value: summary.puzzle_id.toString(),
          }))}
        />
      </View>

      {/* Moves Analysis */}
      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}>
        {currentPuzzle && currentPuzzle.moves_analysis ? (
          <View style={styles.movesContainer}>
            {currentPuzzle.moves_analysis.map(moveData =>
              renderMoveAnalysis(moveData),
            )}
          </View>
        ) : (
          <View style={styles.noMovesState}>
            <CCText fontSize={FontSizes[14]} style={styles.noMovesText}>
              No move analysis available for this puzzle
            </CCText>
          </View>
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: CCColors.White,
    minHeight: 600,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: vs(16),
  },
  title: {
    fontWeight: 'bold',
    color: CCColors?.TextColor?.Primary || '#000000',
  },
  selectorContainer: {
    marginBottom: vs(16),
    zIndex: 3,
  },
  scrollView: {
    flex: 1,
    maxHeight: WINDOW_HEIGHT / 1.2,
  },
  movesContainer: {
    paddingBottom: 20,
  },
  moveSection: {
    marginBottom: vs(20),
    backgroundColor: CCColors.White,
    borderRadius: 8,
    padding: 12,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  moveTitle: {
    fontWeight: 'bold',
    marginBottom: vs(8),
    color: CCColors?.TextColor?.Primary || '#000000',
  },
  correctMoveContainer: {
    backgroundColor: '#E0F2F1', // Light teal/green background
    borderRadius: 6,
    padding: 10,
    marginBottom: vs(12),
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  correctMoveText: {
    fontWeight: '600',
    color: '#00695C', // Darker teal
  },
  percentageText: {
    fontWeight: '500',
  },
  otherMovesSection: {
    marginTop: vs(8),
  },
  otherMovesTitle: {
    fontWeight: '600',
    marginBottom: vs(8),
    color: CCColors?.TextColor?.Primary || '#000000',
  },
  otherMoveItem: {
    paddingVertical: vs(4),
    paddingHorizontal: 8,
  },
  otherMoveText: {
    color: CCColors?.TextColor?.Secondary || '#666666',
  },
  noMovesState: {
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 40,
    paddingHorizontal: 20,
  },
  noMovesText: {
    textAlign: 'center',
    color: CCColors?.TextColor?.Secondary || '#666666',
    fontStyle: 'italic',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 40,
    paddingHorizontal: 20,
  },
  noSummaryImage: {
    width: 120,
    height: 120,
    marginBottom: 16,
  },
  emptyTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
    color: CCColors?.TextColor?.Primary || '#000000',
  },
  emptyDescription: {
    textAlign: 'center',
    color: '#666666',
    lineHeight: 20,
  },
});

export default Summary;
